/**
 * Utility functions for Progress component.
 */

import {
  BoltIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
  PlusCircleIcon,
} from '@heroicons/react/24/outline';
import { ChipVariantProps } from '@heroui/react';

import { useProjects } from '~/hooks/useProjects';

import type { SSEEvent } from '~/types';

/**
 * Format event message for display based on phase and event data.
 *
 * Backend sends clean data (e.g., just task titles), frontend adds formatting
 * based on the phase and available tracking fields (current_step, task_number, etc.).
 *
 * @param event - The SSE event to format
 * @returns Formatted message string for display
 *
 * @example
 * // PRD breakdown progress
 * formatEventMessage(progressEvent) // "Extracted task 5/20: Implement Authentication"
 *
 * // Task generation progress
 * formatEventMessage(progressEvent) // "Enhancing task 17/20: User Profile System"
 *
 * // Connection established
 * formatEventMessage(connectionEvent) // "Initializing"
 */
export function formatEventMessage(event: SSEEvent): string | null {
  // System events
  if (event.event === 'connection_established') {
    return 'Initializing';
  }

  if (event.event === 'heartbeat') {
    return ''; // Don't display heartbeats
  }

  // Agent events - require projectId
  if (!('projectId' in event.data)) {
    return '';
  }

  // Handle prd_progress early - it has a different phase enum
  if (event.event === 'prd_progress') {
    return event.data.message;
  }

  // Handle prd_phase early - it has different data structure (phaseLabel, not message)
  if (event.event === 'prd_phase') {
    return `Phase ${event.data.currentPhase}/${event.data.totalPhases}: ${event.data.phaseLabel}`;
  }

  // Handle PRD Review events with rich context
  if (event.event === 'prd_review_typing') {
    // Only show when actively typing (not when stopping)
    return event.data.isTyping ? event.data.activity || 'Agent is analyzing your message' : null;
  }

  if (event.event === 'prd_review_message') {
    const preview = event.data.preview || event.data.content;
    return preview ? `Agent: ${preview}` : 'Agent sent a message';
  }

  if (event.event === 'prd_review_update') {
    return event.data.changeSummary || 'PRD content updated';
  }

  if (event.event === 'prd_review_error') {
    return 'Review error occurred';
  }

  // Status-changed events (task/project) and task_added don't share BaseAgentEventData's
  // message/phase shape and aren't part of this agent-processing feed - skip them.
  if (
    event.event === 'task_status_changed' ||
    event.event === 'project_status_changed' ||
    event.event === 'task_added'
  ) {
    return null;
  }

  const { data } = event;
  const message = data.message;
  const phase = data.phase;

  switch (event.event) {
    case 'processing':
      // Keep as is: "Analyzing PRD" or task title
      return message;

    case 'progress': {
      // PRD breakdown: show "Extracted task X/Y: Title"
      if (
        phase === 'analysis' &&
        'current_step' in data &&
        'total_steps' in data &&
        data.current_step &&
        data.total_steps
      ) {
        return `Extracted task ${data.current_step}/${data.total_steps}: ${message}`;
      }

      // Task generation: show "Enhancing task X/Y: Title"
      if (
        phase === 'task_generation' &&
        'task_number' in data &&
        'total_tasks' in data &&
        data.task_number &&
        data.total_tasks
      ) {
        return `Enhancing task ${data.task_number}/${data.total_tasks}: ${message}`;
      }

      return message;
    }

    case 'completed': {
      // PRD breakdown: show "Message: X tasks extracted"
      if (phase === 'analysis' && 'total_tasks' in data && data.total_tasks) {
        return `${message}: ${data.total_tasks} tasks extracted`;
      }

      // Task generation: show "Task X/Y enhanced: Title"
      if (
        phase === 'task_generation' &&
        'task_number' in data &&
        'total_tasks' in data &&
        data.task_number &&
        data.total_tasks
      ) {
        return `Task ${data.task_number}/${data.total_tasks} enhanced: ${message}`;
      }

      return message;
    }

    case 'error':
      // Keep error messages as is
      return message;

    case 'task_created':
    case 'task_prompt_created':
    case 'task_prompt_updated':
      // Keep task/prompt messages as is
      return message;

    default:
      return message;
  }
}

/**
 * Format event timestamp for display.
 */
export function formatTimestamp(timestamp: string): string {
  try {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSecs = Math.floor(diffMs / 1000);

    if (diffSecs < 60) {
      return 'just now';
    } else if (diffSecs < 3600) {
      const mins = Math.floor(diffSecs / 60);
      return `${mins}m ago`;
    } else {
      return date.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      });
    }
  } catch {
    return '';
  }
}

/**
 * Get styling for event type.
 *
 * Returns icon, icon color, and chip variant for the event.
 * All events share the same border/background styling (applied in ProgressEvent component).
 */
export function getEventStyle(eventType: SSEEvent['event']): {
  chipColor: ChipVariantProps['color'];
  icon: typeof InformationCircleIcon;
  iconColorClass: string;
} {
  switch (eventType) {
    case 'processing':
      return {
        icon: InformationCircleIcon,
        iconColorClass: 'text-primary-600',
        chipColor: 'primary',
      };
    case 'progress':
      return {
        icon: BoltIcon,
        iconColorClass: 'text-default-500',
        chipColor: 'default',
      };
    case 'completed':
      return {
        icon: CheckCircleIcon,
        iconColorClass: 'text-success-600',
        chipColor: 'success',
      };
    case 'error':
      return {
        icon: ExclamationCircleIcon,
        iconColorClass: 'text-danger-600',
        chipColor: 'danger',
      };
    case 'connection_established':
      return {
        icon: InformationCircleIcon,
        iconColorClass: 'text-primary-600',
        chipColor: 'primary',
      };
    case 'task_created':
      return {
        icon: PlusCircleIcon,
        iconColorClass: 'text-primary-500',
        chipColor: 'default',
      };
    case 'task_prompt_created':
    case 'task_prompt_updated':
      return {
        icon: DocumentTextIcon,
        iconColorClass: 'text-secondary-600',
        chipColor: 'default',
      };
    case 'prd_phase':
    case 'prd_progress':
      return {
        icon: InformationCircleIcon,
        iconColorClass: 'text-success-600',
        chipColor: 'primary',
      };
    default:
      return {
        icon: InformationCircleIcon,
        iconColorClass: 'text-default-500',
        chipColor: 'default',
      };
  }
}

/**
 * Get project name from project ID.
 */
export function useProjectName(projectId: string): string {
  const { projects = [] } = useProjects();
  const project = projects.find(p => p.id === projectId);
  return project?.title || `Project ${projectId.slice(0, 8)}...`;
}
